import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import type { KybFile } from '~/api/kyb/types'

import { KYB_UPLOAD_ENDPOINTS } from './useKybFileUpload.constants'
import type {
  KybUploadEndpoint,
  KybUploadEndpointWithPathParams,
  KybUploadEndpointWithoutPathParams,
} from './useKybFileUpload.types'

export function useKybFileUpload() {
  const $httpFetch = useHttpFetch()
  const logger = useLogger()

  /**
   * Generic file upload function using multipart/form-data (without path params)
   */
  async function uploadFile(
    endpoint: KybUploadEndpointWithoutPathParams,
    file: File,
    options?: {
      additionalFormData?: Record<string, string>
    },
  ): Promise<KybFile>

  /**
   * Generic file upload function using multipart/form-data (with path params)
   */
  async function uploadFile(
    endpoint: KybUploadEndpointWithPathParams,
    file: File,
    options: {
      pathParams: { id: string }
      additionalFormData?: Record<string, string>
    },
  ): Promise<KybFile>

  /**
   * Implementation
   */
  async function uploadFile(
    endpoint: KybUploadEndpoint,
    file: File,
    options?: {
      pathParams?: Record<string, string>
      additionalFormData?: Record<string, string>
    },
  ): Promise<KybFile> {
    try {
      const formData = new FormData()
      formData.append('file', file)

      // Add any additional form data if needed (e.g., metadata, document type)
      if (options?.additionalFormData) {
        Object.entries(options.additionalFormData).forEach(([key, value]) => {
          formData.append(key, value)
        })
      }

      return await $httpFetch(endpoint, {
        method: 'POST',
        body: formData,
        pathParams: options?.pathParams as Record<string, string>,
      })
    } catch (error) {
      logger.error(`Failed to upload file: ${file.name} to ${endpoint}`, {
        error: error instanceof Error ? error : undefined,
        fileSize: file.size,
        fileType: file.type,
        owners: ['bot-squad-seller-experience-front'],
      })

      throw error
    }
  }

  function uploadProofOfSecondHandDealerLicence(file: File) {
    return uploadFile(
      KYB_UPLOAD_ENDPOINTS.PROOF_OF_SECOND_HAND_DEALER_LICENCE,
      file,
    )
  }

  function uploadArticleOfIncorporation(file: File) {
    return uploadFile(KYB_UPLOAD_ENDPOINTS.ARTICLE_OF_INCORPORATION, file)
  }

  function uploadW9(file: File) {
    return uploadFile(KYB_UPLOAD_ENDPOINTS.W9, file)
  }

  function uploadIdentityDocument(stakeholderId: string, file: File) {
    return uploadFile(KYB_UPLOAD_ENDPOINTS.PROOF_OF_IDENTITY, file, {
      pathParams: { id: stakeholderId },
    })
  }

  function uploadLetterOfAuthorization(file: File) {
    return uploadFile(KYB_UPLOAD_ENDPOINTS.LETTER_OF_AUTHORIZATION, file)
  }

  return {
    uploadProofOfSecondHandDealerLicence,
    uploadArticleOfIncorporation,
    uploadW9,
    uploadIdentityDocument,
    uploadLetterOfAuthorization,
  }
}
